import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ComponentProps } from 'react';

import { mutatePixelUpdate, type PixelType, type usePixelById } from '@/api/pixel';
import { LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { Panel } from '@/components/panels';
import { ReturnToLink } from '@/components/ReturnToLink';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import PixelForm, { getPixelFormDefaultValues, getPixelFormSchema } from '../PixelForm';
import PixelEditDetails from './PixelEditDetails';

type PixelEditContentProps = {
  pixel: PixelType;
  mutatePixelById: ReturnType<typeof usePixelById>['mutate'];
};

const PixelEditContent = ({ pixel, mutatePixelById }: PixelEditContentProps) => {
  const navigate = useInstanceNavigate();
  const { pushNotification } = useNotification();
  const form = useAppForm({
    defaultValues: getPixelFormDefaultValues(pixel),
    validators: {
      onSubmit: getPixelFormSchema(),
      onSubmitAsync: async ({ value }) => {
        try {
          const { audience, channel, vendor, name, description } = value;
          if (!audience || !channel || !vendor) {
            return;
          }
          const updatedPixel = { ...pixel, ...value } as PixelType;

          await mutatePixelUpdate({
            id: pixel.id,
            name,
            description,
            channel_id: channel.id,
            vendor_id: vendor.id,
            audience_id: audience.id,
          });

          mutatePixelById({
            pixels: {
              getById: updatedPixel,
            },
          });

          navigate(ROUTES.pixels.path, {
            state: {
              successMessage: t`Pixel was successfully updated.`,
              optimisticUpdate: updatedPixel,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        }
      },
    },
  });
  const pixelForm = form as ComponentProps<typeof PixelForm>['form'];

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex w-full flex-col gap-6">
          <div className="flex items-center justify-between">
            <ReturnToLink route={ROUTES.pixels.path} routeName={t`Pixel Overview`} />
            <div className="flex gap-4">
              <LinkButton to={ROUTES.pixels.path}>
                <Trans>Cancel</Trans>
              </LinkButton>
              <form.SubmitButton variant="primary">
                <Trans>Save</Trans>
              </form.SubmitButton>
            </div>
          </div>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Panel title={<Trans>Edit Pixel</Trans>}>
                <div className="flex flex-col gap-10">
                  <PixelForm form={pixelForm} pixel={pixel} disabled={isSubmitting} isEdit />
                  <PixelEditDetails pixel={pixel} />
                </div>
              </Panel>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  );
};

export default PixelEditContent;
